import React, { useState, useEffect } from 'react';
import { Modal, Switch, Button, message } from 'antd';
import { IonIcon } from '@ionic/react';
import { globeOutline, lockClosedOutline, informationCircleOutline, documentOutline, linkOutline, copyOutline, checkmarkOutline } from 'ionicons/icons';
import { generateShareLink, getFileMetadata } from '../../../lib/firebase/firebase';
import { isEmbroideryTrace } from '../../../config/appVariant';
import './PrivacyPopup.css';

const PrivacyPopup = ({ 
  isOpen, 
  onClose, 
  file, 
  onPrivacyChange,
  isUpdating = false 
}) => {
  // Helper function to determine if file should be considered public
  const getFilePublicStatus = (file) => {
    // All files can now be public/private, regardless of type
    // If isPublic is explicitly set, use that value
    if (file?.isPublic !== undefined) {
      return file.isPublic === true;
    }
    // Default: all files are private by default (since you set both to false)
    return false;
  };

  const [localIsPublic, setLocalIsPublic] = useState(() => getFilePublicStatus(file));
  const [linkCopied, setLinkCopied] = useState(false);
  const [fileTitle, setFileTitle] = useState('');
  const [isLoadingTitle, setIsLoadingTitle] = useState(false);

  // SvgTrace only. EmbroideryTrace sharing is in-app, through the Community tab,
  // and generateShareLink hardcodes svgtrace.com — which resolves ids against
  // svgMetadata, so an embroiderymetadata id there is simply not found.
  const showShareLink = !isEmbroideryTrace && getFilePublicStatus(file);

  // Fetch the file metadata to get the proper title for SEO - always fetch fresh from Firestore
  useEffect(() => {
    const fetchFileTitle = async () => {
      // The title is only used to build the share-link slug, so skip the read
      // entirely where no link is shown.
      if (!isOpen || !file?.id || !showShareLink) return;
      
      // Always fetch fresh from Firestore to ensure we have the latest title
      setIsLoadingTitle(true);
      setFileTitle(''); // Clear previous title while loading
      
      try {
        const metadata = await getFileMetadata(file.id);
        if (metadata) {
          setFileTitle(metadata.seoTitle || metadata.prompt || '');
        }
      } catch (error) {
        console.error('Error fetching file metadata:', error);
        // Fall back to local values if fetch fails
        setFileTitle(file.seoTitle || file.prompt || '');
      } finally {
        setIsLoadingTitle(false);
      }
    };
    
    fetchFileTitle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, file?.id, showShareLink]);

  // Generate a display-friendly version of the share link
  const getDisplayShareLink = () => {
    if (!file?.id) return 'Loading...';
    if (isLoadingTitle) return 'Loading share link...';
    
    const title = fileTitle || file.seoTitle || file.prompt || '';
    // Create slug from title
    const slug = title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 30);
    
    if (slug) {
      return `svgtrace.com/community/${slug}...`;
    }
    return `svgtrace.com/community/${file.id.substring(0, 8)}...`;
  };

  const handleToggle = (checked) => {
    setLocalIsPublic(checked);
  };

  const handleCopyShareLink = async () => {
    if (!file?.id || isLoadingTitle) return;
    
    try {
      // Use the fetched title for the slug (ensures SEO-friendly URL)
      const title = fileTitle || file.seoTitle || file.prompt || '';
      const shareLink = generateShareLink(file.id, title);
      await navigator.clipboard.writeText(shareLink);
      setLinkCopied(true);
      message.success('Share link copied to clipboard!');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      console.error('Error copying share link:', error);
      message.error('Failed to copy share link');
    }
  };

  const handleSave = () => {
    onPrivacyChange(localIsPublic);
  };

  const handleCancel = () => {
    setLocalIsPublic(getFilePublicStatus(file));
    onClose();
  };

  // All file types can now be shared publicly
  const isUploadedFile = false; // Disabled restriction - all files can be public

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      width={400}
      className="privacy-popup-modal"
      centered
      maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      destroyOnClose={true}
    >
      <div className="privacy-popup-content">
        {/* Header */}
        <div className="privacy-popup-header">
          <div className="privacy-popup-icon">
            <IonIcon 
              icon={localIsPublic ? globeOutline : lockClosedOutline} 
              className={localIsPublic ? "public-icon" : "private-icon"}
            />
          </div>
          <div className="privacy-popup-title">
            <h3>Privacy Settings</h3>
            <p>Control who can see this design</p>
          </div>
        </div>

        {/* Content */}
        <div className="privacy-popup-body">
          {isUploadedFile && (
            <div className="privacy-info-note" style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fff7e6', borderRadius: '6px', border: '1px solid #ffd591' }}>
              <IonIcon icon={informationCircleOutline} style={{ color: '#fa8c16' }} />
              <span style={{ color: '#d48806', fontWeight: '500' }}>Uploaded files must remain private and cannot be shared publicly</span>
            </div>
          )}
          
          <div className="privacy-option">
            <div className="privacy-option-info">
              <div className="privacy-option-title">
                <IonIcon icon={globeOutline} className="option-icon public" />
                <span>Public</span>
              </div>
              <p className="privacy-option-description">
                {isUploadedFile 
                  ? "Only generated files can be shared in the community gallery" 
                  : "Anyone can discover and view this design in the community gallery"
                }
              </p>
            </div>
            <Switch
              checked={localIsPublic}
              onChange={handleToggle}
              className="privacy-switch"
              disabled={isUpdating || isUploadedFile}
            />
          </div>

          <div className={`privacy-option ${!localIsPublic ? 'active' : ''}`}>
            <div className="privacy-option-info">
              <div className="privacy-option-title">
                <IonIcon icon={lockClosedOutline} className="option-icon private" />
                <span>Private</span>
              </div>
              <p className="privacy-option-description">
                Only you can see this design. It won't appear in the community gallery
              </p>
            </div>
          </div>

          {/* Share Link Section - Only show when file is currently public */}
          {showShareLink && (
            <div className="privacy-share-section">
              <div className="privacy-share-header">
                <IonIcon icon={linkOutline} className="share-icon" />
                <span>Share Link</span>
              </div>
              <div className="privacy-share-content">
                <div className="privacy-share-link-box">
                  <span className="privacy-share-link-text">
                    {getDisplayShareLink()}
                  </span>
                  <button 
                    className={`privacy-copy-btn ${linkCopied ? 'copied' : ''}`}
                    onClick={handleCopyShareLink}
                    disabled={!file?.id || isLoadingTitle}
                  >
                    <IonIcon icon={linkCopied ? checkmarkOutline : copyOutline} />
                    {isLoadingTitle ? 'Loading...' : linkCopied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="privacy-share-description">
                  Anyone with this link can view and download your design
                </p>
              </div>
            </div>
          )}

          <div className="privacy-info-note">
            <IonIcon icon={informationCircleOutline} />
            <span>You can change this setting anytime</span>
          </div>
        </div>

        {/* Footer */}
        <div className="privacy-popup-footer">
          <Button 
            onClick={handleCancel}
            disabled={isUpdating}
            className="privacy-cancel-btn"
          >
            Cancel
          </Button>
          <Button 
            type="primary"
            onClick={handleSave}
            loading={isUpdating}
            className="privacy-save-btn"
            disabled={localIsPublic === getFilePublicStatus(file) || (isUploadedFile && localIsPublic)}
          >
            {isUpdating ? 'Updating...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PrivacyPopup; 